import { ReviewStatus, TestTarget, UseCase, Policy } from "../types/TestTarget";

// ステータスに応じた色の定義
export const STATUS_COLORS: Record<ReviewStatus, string> = {
  draft: "#9E9E9E",           // グレー
  need_review: "#FF9800",     // オレンジ
  in_review: "#2196F3",       // ブルー
  approved: "#4CAF50",        // グリーン
  rejected: "#F44336",        // レッド
  archived: "#757575",        // ダークグレー
};

// ステータスに応じた背景色の定義（薄い色）
export const STATUS_BG_COLORS: Record<ReviewStatus, string> = {
  draft: "#F5F5F5",
  need_review: "#FFF3E0",
  in_review: "#E3F2FD",
  approved: "#E8F5E9",
  rejected: "#FFEBEE",
  archived: "#EEEEEE",
};

// ステータスの日本語ラベル
export const STATUS_LABELS: Record<ReviewStatus, string> = {
  draft: "下書き",
  need_review: "レビュー待ち",
  in_review: "レビュー中",
  approved: "承認済み",
  rejected: "却下",
  archived: "アーカイブ",
};

// ステータスを取得
export function getStatus(
  item: TestTarget | UseCase | Policy | { status?: ReviewStatus; params?: any } | undefined
): ReviewStatus {
  if (!item) return "approved";

  // TestTargetの場合、params.statusをチェック
  if ('params' in item && item.params && typeof item.params === 'object' && 'status' in item.params) {
    return item.params.status as ReviewStatus;
  }

  // UseCase/Policyなど、直接statusフィールドがある場合
  if ('status' in item && item.status) return item.status;

  // デフォルトは承認済み
  return "approved";
}

// TestTargetがneed_reviewかどうかをチェック
export function isNeedReview(target: TestTarget): boolean {
  return getStatus(target) === "need_review";
}

// ステータスがレビュー待ち状態（下書き、レビュー待ち、レビュー中）かチェック
export function isPendingReview(status: ReviewStatus): boolean {
  return status === "draft" || status === "need_review" || status === "in_review";
}

// TestTargetに関連するすべての要素がneed_reviewかチェック
export function hasNeedReviewItems(target: TestTarget): {
  target: boolean;
  usecases: string[]; // need_reviewなユースケースのID
  policies: string[]; // need_reviewなポリシーのID
  transitions: number[]; // need_reviewな遷移のインデックス
} {
  const result = {
    target: isNeedReview(target),
    // usecase/policy は procedure モデルへ移行したため常に空（型 shape のみ維持）
    usecases: [] as string[],
    policies: [] as string[],
    transitions: [] as number[],
  };

  // 遷移をチェック
  if (target.params && "transitions" in target.params) {
    const transitions = target.params.transitions || [];
    transitions.forEach((transition, index) => {
      if (getStatus(transition) === "need_review") {
        result.transitions.push(index);
      }
    });
  }

  return result;
}

// TestTargetに関連するすべての要素にレビュー待ち状態（下書き、レビュー待ち、レビュー中）があるかチェック
// allTargetsを受け取り、View／コンポーネント配下の全ての子ターゲットもチェックする
export function hasPendingReviewItems(
  target: TestTarget,
  allTargets?: TestTarget[]
): {
  target: boolean;
  usecases: string[]; // レビュー待ち状態のユースケースのID
  policies: string[]; // レビュー待ち状態のポリシーのID
  transitions: number[]; // レビュー待ち状態の遷移のインデックス
  childTargets: string[]; // レビュー待ち状態の子ターゲットのID
} {
  const result = {
    target: isPendingReview(getStatus(target)),
    // usecase/policy は procedure モデルへ移行したため常に空（型 shape のみ維持）
    usecases: [] as string[],
    policies: [] as string[],
    transitions: [] as number[],
    childTargets: [] as string[],
  };

  // 遷移をチェック
  if (target.params && "transitions" in target.params) {
    const transitions = target.params.transitions || [];
    transitions.forEach((transition, index) => {
      if (isPendingReview(getStatus(transition))) {
        result.transitions.push(index);
      }
    });
  }

  // View またはコンポーネント配下の全ての子ターゲット（section、overlay、item 等）をチェック
  if (
    allTargets &&
    (target.type === "view" || target.type === "component")
  ) {
    const checkChildTargets = (parentId: string) => {
      const children = allTargets.filter((t) => t.parentId === parentId);

      children.forEach((child) => {
        // 子ターゲット自体のステータスをチェック
        if (isPendingReview(getStatus(child))) {
          result.childTargets.push(child.id);
        }

        // 子ターゲットの遷移をチェック
        if (child.params && "transitions" in child.params) {
          const childTransitions = child.params.transitions || [];
          childTransitions.forEach((transition, index) => {
            if (isPendingReview(getStatus(transition))) {
              result.transitions.push(index);
            }
          });
        }

        // さらに孫ターゲットもチェック（再帰的）
        checkChildTargets(child.id);
      });
    };

    checkChildTargets(target.id);
  }

  return result;
}

// 色を取得
export function getStatusColor(status: ReviewStatus): string {
  return STATUS_COLORS[status] || STATUS_COLORS.approved;
}

// 背景色を取得
export function getStatusBgColor(status: ReviewStatus): string {
  return STATUS_BG_COLORS[status] || STATUS_BG_COLORS.approved;
}

// ラベルを取得
export function getStatusLabel(status: ReviewStatus): string {
  return STATUS_LABELS[status] || status;
}

// サービス配下の全てのレビュー待ち件数をカウント
export function countPendingReviewItems(
  serviceId: string,
  allTargets: TestTarget[]
): number {
  let count = 0;

  // サービス配下の全てのターゲットを取得（再帰的）
  const getDescendants = (parentId: string): TestTarget[] => {
    const children = allTargets.filter((t) => t.parentId === parentId);
    const descendants: TestTarget[] = [];

    children.forEach((child) => {
      descendants.push(child);
      descendants.push(...getDescendants(child.id));
    });

    return descendants;
  };

  const descendants = getDescendants(serviceId);

  // 各ターゲットのレビュー待ち状態をチェック
  descendants.forEach((target) => {
    const pendingInfo = hasPendingReviewItems(target, allTargets);

    // ターゲット自体
    if (pendingInfo.target) count++;

    // usecase/policy は procedure モデルへ移行したためカウント対象外

    // 遷移
    count += pendingInfo.transitions.length;

    // 子ターゲット（重複を避けるため、descendantsに含まれないものだけカウント）
    // ただし、hasPendingReviewItemsは既にchildTargetsをカウントしているので除外
  });

  return count;
}
